// Single source of truth for everything related to the companion mobile app
// (store links, custom scheme, device detection). Used by the install banner,
// the QR-code modal, and the floating button.

export const IOS_URL =
  process.env.REACT_APP_IOS_APP_URL ||
  "https://apps.apple.com/app/id6759987248";

export const ANDROID_URL =
  process.env.REACT_APP_ANDROID_APP_URL ||
  "https://play.google.com/store/apps/details?id=com.hexaflow.mbmo";

// Custom URL scheme declared by the Expo app (app.config.ts → scheme: "mbmo").
// Opening this from a user gesture launches the app if it is installed.
export const APP_SCHEME = "mbmo://";

export type MobilePlatform = "ios" | "android" | null;

/** Detected mobile platform, or null on desktop. */
export function detectMobilePlatform(): MobilePlatform {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as "MacIntel" with touch points, so check that too.
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/Android/.test(ua)) return "android";
  return null;
}

export function isMobileDevice(): boolean {
  return detectMobilePlatform() !== null;
}

/** Store URL for the given platform (defaults to the detected one). */
export function getStoreUrl(platform: MobilePlatform = detectMobilePlatform()): string {
  return platform === "android" ? ANDROID_URL : IOS_URL;
}

/**
 * Try to open the native app via its custom scheme; if it doesn't take over
 * (app not installed) fall back to the store after a short delay. Must be
 * called from a user gesture (button tap) — browsers block scheme launches
 * otherwise. Universal `https://` links are deliberately NOT used here: Safari
 * suppresses them for same-domain navigations, so the scheme is the only thing
 * that reliably jumps into the app from within the web app.
 */
export function openAppOrStore(): void {
  const platform = detectMobilePlatform();
  const storeUrl = getStoreUrl(platform);

  let appOpened = false;
  const markOpened = () => {
    // The page is hidden/backgrounded when the app actually takes over.
    if (document.visibilityState === "hidden") appOpened = true;
  };
  document.addEventListener("visibilitychange", markOpened);
  window.addEventListener("pagehide", markOpened);

  const timer = window.setTimeout(() => {
    document.removeEventListener("visibilitychange", markOpened);
    window.removeEventListener("pagehide", markOpened);
    if (!appOpened && document.visibilityState === "visible") {
      window.location.href = storeUrl;
    }
  }, 1500);

  // Cancel the store fallback if we get a visibility change before the timer.
  window.addEventListener(
    "pagehide",
    () => window.clearTimeout(timer),
    { once: true },
  );

  window.location.href = APP_SCHEME;
}
